import OrgManagement from './OrgManagement'
import Overview from './Overview'
import PersonDetails from './People/PersonDetails'
import People from './People/People'
import Reports from './Reports/Reports'
import Imports from './Imports/Imports'
import Integrations from './Integrations/Integrations'
import IntegrationConfiguration from './Integrations/IntegrationConfiguration'
import ConnectionDetails from './Integrations/ConnectionDetails'
import ResourceDetails from './Integrations/ResourceDetails'
import Permissions from './Permissions/Permissions'
import Categories from './Categories/Categories'
import CategoryDetails from './Categories/CategoryDetails'
import Groups from './Groups/Groups'
import GroupDetails from './Groups/GroupDetails'
import Settings from './Settings/Settings'
import UserChatActivity from './Activity/UserChatActivity'

export { OrgManagement as default }

export const overviewRoute = {
	path: 'overview',
	name: 'overview',
	component: Overview,
}

export const userDetailsRoute = {
	path: 'users/:id',
	name: 'user',
	component: PersonDetails,
	props: (route) => ({ userId: route.params.id }),
	meta: { titleKey: 'settings_orgprofile_people' },
}

export const usersRoute = {
	path: 'users',
	name: 'users',
	component: People,
	props: (route) => ({ searchTerm: route.query.q }),
	meta: { titleKey: 'settings_orgprofile_people' },
}

export const reportsRoute = {
	path: 'reports',
	name: 'reports',
	component: Reports,
	meta: { titleKey: 'admin_overview_reports_title' },
}

export const importsRoute = {
	path: 'imports',
	name: 'imports',
	component: Imports,
	meta: { titleKey: 'admin_sidebar_imports_label' },
}

export const importDetailsRoute = {
	path: 'imports/:id',
	name: 'import',
	component: Imports,
	meta: { titleKey: 'admin_sidebar_imports_label' },
}

export const integrationsRoute = {
	path: 'integrations/:type?',
	name: 'integrations',
	component: Integrations,
	meta: {
		titleKey: 'admin_overview_integrations_title',
	},
}

export const integrationConfigurationRoute = {
	path: 'integrations/:id/configuration',
	name: 'configuration',
	component: IntegrationConfiguration,
	props: (route) => ({
		integrationId: route.params.id,
		integrationObject: route.query.integrationObject
			? JSON.parse(route.query.integrationObject)
			: null,
		connectionId: route.query.connectionId,
		platform: route.query.platform,
	}),
	meta: {
		titleKey: 'admin_overview_integrations_title',
	},
}

export const connectionDetailsRoute = {
	path: 'integrations/connections/:id',
	name: 'connection',
	component: ConnectionDetails,
	props: (route) => ({
		connectionId: route.params.id,
		integrationType: route.query.integrationType,
	}),
	meta: {
		titleKey: 'admin_overview_integrations_title',
	},
}

export const resourceDetailsRoute = {
	path: 'integrations/connections/:connectionId/resources/:resourceId',
	name: 'resource',
	component: ResourceDetails,
	props: (route) => ({
		connectionId: route.params.connectionId,
		resourceId: route.params.resourceId,
		integrationName: route.query.integrationName,
	}),
	meta: {
		titleKey: 'admin_overview_integrations_title',
	},
}

export const settingsRoute = {
	path: 'settings',
	name: 'settings',
	component: Settings,
	meta: { titleKey: 'common_settingsapp' },
}

export const permissionsRoute = {
	path: 'permissions',
	name: 'permissions',
	component: Permissions,
	meta: { titleKey: 'admin_sidebar_permissions_label' },
}

export const categoriesRoute = {
	path: 'categories',
	name: 'categories',
	component: Categories,
	meta: { titleKey: 'admin_categories_title' },
}

export const categoryDetailsRoute = {
	path: 'categories/:id',
	name: 'category',
	component: CategoryDetails,
	props: (route) => ({
		categoryId: route.params.id,
		openNewGroupModal: !!route.query.newGroup,
	}),
	meta: { titleKey: 'admin_categories_title' },
}

export const groupsRoute = {
	path: 'groups',
	name: 'groups',
	component: Groups,
	props: (route) => ({
		searchTerm: route.query.q,
		showNewModal: !!route.query.new,
	}),
	meta: { titleKey: 'admin_categories_groups_label' },
}

export const groupDetailsRoute = {
	path: 'groups/:id',
	name: 'group',
	component: GroupDetails,
	props: (route) => ({
		groupId: route.params.id,
		cachedGroup: route.params.group,
	}),
	meta: { titleKey: 'admin_categories_groups_label' },
}

// Admin-flow activity routes — nested under mainRoute so they share the
// OrgManagement wrapper (sidebar, role check). Used from the
// GroupDetails/PersonDetails dots menus.
export const userActivityFromGroupRoute = {
	path: 'groups/:chatId/users/:userId/activity',
	name: 'group-user-activity',
	component: UserChatActivity,
	props: (route) => ({
		chatId: route.params.chatId,
		userId: route.params.userId,
	}),
	meta: { titleKey: 'admin_activity_title' },
}

export const userActivityFromUserRoute = {
	path: 'users/:userId/chats/:chatId/activity',
	name: 'user-chat-activity',
	component: UserChatActivity,
	props: (route) => ({
		chatId: route.params.chatId,
		userId: route.params.userId,
	}),
	meta: { titleKey: 'admin_activity_title' },
}

// Iframe-only activity routes — top-level, intentionally NOT nested under
// mainRoute so they bypass the OrgManagement wrapper (no sidebar, no admin
// role check). Backend's 403 is the auth surface; UserChatActivity handles
// that path. Used for Canvas/LTI iframe launches.
export const iframeUserActivityFromGroupRoute = {
	path: '/activity/groups/:chatId/users/:userId',
	name: 'iframe-group-user-activity',
	component: UserChatActivity,
	props: (route) => ({
		chatId: route.params.chatId,
		userId: route.params.userId,
	}),
	meta: { titleKey: 'admin_activity_title' },
}

export const iframeUserActivityFromUserRoute = {
	path: '/activity/users/:userId/chats/:chatId',
	name: 'iframe-user-chat-activity',
	component: UserChatActivity,
	props: (route) => ({
		chatId: route.params.chatId,
		userId: route.params.userId,
	}),
	meta: { titleKey: 'admin_activity_title' },
}

export const mainRoute = {
	path: '/organization',
	name: 'org',
	component: OrgManagement,
	meta: { titleKey: 'settings_option_organization' },
	children: [
		overviewRoute,
		usersRoute,
		userDetailsRoute,
		userActivityFromUserRoute,
		groupsRoute,
		groupDetailsRoute,
		userActivityFromGroupRoute,
		categoriesRoute,
		categoryDetailsRoute,
		reportsRoute,
		importsRoute,
		importDetailsRoute,
		integrationsRoute,
		integrationConfigurationRoute,
		connectionDetailsRoute,
		resourceDetailsRoute,
		permissionsRoute,
		settingsRoute,
	],
}

export const routes = [
	iframeUserActivityFromGroupRoute,
	iframeUserActivityFromUserRoute,
	mainRoute,
]
